import { faChevronRight, faChevronUp } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { EmblaCarouselType, EmblaOptionsType } from "embla-carousel";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import useEmblaCarousel from "embla-carousel-react";
import React, { Children, cloneElement, PropsWithChildren, useCallback, useEffect, useState } from "react";
import { Button } from "semantic-ui-react";
import "./Carousel.scss";
import classNames from "classnames";

type PropType = {
  options?: EmblaOptionsType;
  children?: React.ReactNode;
  hideControls?: boolean;
  scrollTo?: number | null;
  className?: string;
  direction?: "horizontal" | "vertical";
  showScrollGradients?: boolean;
  leftArrow?: React.ReactNode;
  rightArrow?: React.ReactNode;
};

const Carousel: React.FC<PropType> = ({
  children,
  options,
  hideControls,
  scrollTo,
  className,
  direction = "horizontal",
  showScrollGradients = true,
  leftArrow,
  rightArrow,
}) => {
  const emblaOptions: EmblaOptionsType = {
    axis: direction === "vertical" ? "y" : "x",
    ...options,
  };

  const [emblaRef, emblaApi] = useEmblaCarousel(emblaOptions, [WheelGesturesPlugin()]);
  const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } = usePrevNextButtons(emblaApi);
  const { canScrollPrev, canScrollNext } = useScrollGradients(emblaApi);
  const count = Children.count(children);

  useEffect(() => {
    if (scrollTo === null || scrollTo === undefined) return;
    emblaApi?.scrollTo(scrollTo);
  }, [scrollTo]);

  if (count === 0) return null;

  const isVertical = direction === "vertical";

  return (
    <section
      className={classNames("carousel tw-relative", className, {
        "carousel--vertical": isVertical,
        "carousel--horizontal": !isVertical,
      })}
    >
      {!hideControls &&
        (leftArrow ? (
          cloneElement(leftArrow as React.ReactElement, { onClick: onPrevButtonClick, disabled: prevBtnDisabled })
        ) : (
          <PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} direction={direction} />
        ))}

      {showScrollGradients && (
        <>
          {/* Start gradient (top for vertical, left for horizontal) */}
          <div
            className={classNames("tw-pointer-events-none tw-absolute tw-z-40 tw-transition-opacity tw-duration-200", {
              // Horizontal - left gradient
              "tw-left-0 tw-top-0 tw-h-full tw-w-[88px] tw-bg-gradient-to-r tw-from-white tw-to-transparent":
                !isVertical,
              // Vertical - top gradient
              "tw-left-0 tw-top-0 tw-h-[88px] tw-w-full tw-bg-gradient-to-b tw-from-white tw-to-transparent":
                isVertical,
              "tw-opacity-100": canScrollPrev,
              "tw-opacity-0": !canScrollPrev,
            })}
          />

          {/* End gradient (bottom for vertical, right for horizontal) */}
          <div
            className={classNames("tw-pointer-events-none tw-absolute tw-z-40 tw-transition-opacity tw-duration-200", {
              // Horizontal - right gradient
              "tw-right-0 tw-top-0 tw-h-full tw-w-[88px] tw-bg-gradient-to-l tw-from-white tw-to-transparent":
                !isVertical,
              // Vertical - bottom gradient
              "tw-bottom-0 tw-left-0 tw-h-[88px] tw-w-full tw-bg-gradient-to-t tw-from-white tw-to-transparent":
                isVertical,
              "tw-opacity-100": canScrollNext,
              "tw-opacity-0": !canScrollNext,
            })}
          />
        </>
      )}

      <div className='carousel-viewport' ref={emblaRef}>
        <div
          className={classNames("carousel-container", {
            "carousel-container--vertical": isVertical,
          })}
        >
          {children}
        </div>
      </div>
      {!hideControls &&
        (rightArrow ? (
          cloneElement(rightArrow as React.ReactElement, { onClick: onNextButtonClick, disabled: nextBtnDisabled })
        ) : (
          <NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} direction={direction} />
        ))}
    </section>
  );
};
export default Carousel;

type UsePrevNextButtonsType = {
  prevBtnDisabled: boolean;
  nextBtnDisabled: boolean;
  onPrevButtonClick: () => void;
  onNextButtonClick: () => void;
};

const usePrevNextButtons = (emblaApi: EmblaCarouselType | undefined): UsePrevNextButtonsType => {
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
  }, [emblaApi]);

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    emblaApi.on("reInit", onSelect).on("select", onSelect);
  }, [emblaApi, onSelect]);

  return {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  };
};

type UseScrollGradientsType = {
  canScrollPrev: boolean;
  canScrollNext: boolean;
};

const useScrollGradients = (emblaApi: EmblaCarouselType | undefined): UseScrollGradientsType => {
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    emblaApi.on("reInit", onSelect).on("select", onSelect);
  }, [emblaApi, onSelect]);

  return {
    canScrollPrev,
    canScrollNext,
  };
};

function PrevButton({
  children,
  onClick,
  disabled,
  direction = "horizontal",
}: PropsWithChildren<{
  onClick: () => void;
  disabled: boolean;
  direction?: "horizontal" | "vertical";
}>) {
  const isVertical = direction === "vertical";

  return (
    <div
      className={classNames(
        "tw-pointer-events-none tw-absolute tw-z-50 tw-flex tw-items-center tw-transition-opacity tw-duration-100 tw-ease-out",
        {
          "tw-left-0 tw-top-0 tw-h-full tw-w-36 tw-justify-start tw-bg-gradient-to-l tw-from-transparent tw-to-white":
            !isVertical,
          "tw-left-0 tw-top-0 tw-h-36 tw-w-full tw-justify-center tw-bg-gradient-to-b tw-from-white tw-to-transparent":
            isVertical,
          "tw-opacity-0": disabled,
          "tw-opacity-100": !disabled,
        },
      )}
    >
      <Button
        secondary
        className='tw-pointer-events-auto tw-z-50 tw-m-0 tw-h-10 tw-w-10 tw-p-0 tw-shadow-md'
        onClick={onClick}
        disabled={disabled}
      >
        <FontAwesomeIcon icon={isVertical ? faChevronUp : faChevronRight} rotation={isVertical ? undefined : 180} />
        {children}
      </Button>
    </div>
  );
}

function NextButton({
  children,
  onClick,
  disabled,
  direction = "horizontal",
}: PropsWithChildren<{
  onClick: () => void;
  disabled: boolean;
  direction?: "horizontal" | "vertical";
}>) {
  const isVertical = direction === "vertical";

  return (
    <div
      className={classNames(
        "tw-pointer-events-none tw-absolute tw-flex tw-items-center tw-transition-opacity tw-duration-100 tw-ease-out",
        {
          "tw-right-0 tw-top-0 tw-h-full tw-w-36 tw-justify-end tw-bg-gradient-to-r tw-from-transparent tw-to-white":
            !isVertical,
          "tw-bottom-0 tw-left-0 tw-h-36 tw-w-full tw-justify-center tw-bg-gradient-to-t tw-from-white tw-to-transparent":
            isVertical,
          "tw-opacity-0": disabled,
          "tw-opacity-100": !disabled,
        },
      )}
    >
      <Button
        secondary
        className='tw-pointer-events-auto tw-z-50 tw-m-0 tw-h-10 tw-w-10 tw-p-0 tw-shadow-md'
        onClick={onClick}
        disabled={disabled}
      >
        <FontAwesomeIcon icon={isVertical ? faChevronUp : faChevronRight} rotation={isVertical ? 180 : undefined} />
        {children}
      </Button>
    </div>
  );
}
